import { api } from './client';
import { downloadBlob } from './iaaExports';

/** SAA reference matrix (#11). Profiles / asset-classes are referenced by their
 *  immutable `code`. Reads are open to any authenticated user; writes are gated
 *  `saa.manage` (admin) at the backend. */

export interface SaaProfile {
  code: string;
  label: string;
  sort_order: number;
}

export interface SaaAssetClass {
  code: string;
  label: string;
  parent_code: string | null;
  benchmark_ticker: string | null;
  sort_order: number;
}

export interface SaaAllocationCell {
  asset_code: string;
  asset_label: string;
  parent_code: string | null;
  benchmark_ticker: string | null;
  asset_sort_order: number;
  min_pct: number;
  neutral_pct: number | null;
  max_pct: number;
}

export interface SaaProfileMatrix {
  code: string;
  label: string;
  cells: SaaAllocationCell[];
}

export interface SaaProfileCreate {
  code: string;
  label: string;
  sort_order: number;
}
export interface SaaProfileUpdate {
  label?: string;
  sort_order?: number;
}
export interface SaaAssetClassCreate {
  code: string;
  label: string;
  parent_code?: string | null;
  benchmark_ticker?: string | null;
  sort_order: number;
}
export interface SaaAssetClassUpdate {
  label?: string;
  parent_code?: string | null;
  benchmark_ticker?: string | null;
  sort_order?: number;
}
export interface SaaAllocationUpsert {
  min_pct: number;
  neutral_pct?: number | null;
  max_pct: number;
}

/** SAA daily-returns reference store (Item C). */
export type SaaReturnsImportMode = 'preview' | 'commit';
export type SaaReturnsImportConflict = 'skip' | 'overwrite';

export interface SaaReturnsImportReport {
  mode: SaaReturnsImportMode;
  on_conflict: SaaReturnsImportConflict;
  total_cells: number;
  profiles_matched: number;
  profile_labels: string[];
  unknown_columns: string[];
  n_dates: number;
  date_from: string | null;
  date_to: string | null;
  new_count: number;
  conflict_count: number;
  errors: string[];
  inserted: number;
  overwritten: number;
  skipped: number;
}

export interface SaaReturnsSeriesPoint {
  date: string;
  cumulative_return: number;
}
export interface SaaReturnsSeries {
  profile_code: string;
  profile_label: string;
  first: string | null;
  last: string | null;
  n_days: number;
  period_from: string | null;
  period_to: string | null;
  period_return: number | null;
  points: SaaReturnsSeriesPoint[];
}

/** SAA-series analytics (#4, Inc 2). Daily-basis statistics + curves for one profile. */
export interface SaaDrawdownPoint { date: string; drawdown: number }
export interface SaaMonthlyReturn { year: number; month: number; ret: number }
export interface SaaYearlyReturn { year: number; ret: number }
export interface SaaProfileAnalytics {
  profile_code: string;
  profile_label: string;
  first: string | null;
  last: string | null;
  n_days: number;
  period_from: string | null;
  period_to: string | null;
  period_return: number | null;
  cagr: number | null;
  ann_volatility: number | null;
  max_drawdown: number;          // most-negative drawdown (≤ 0)
  max_drawdown_start: string | null;  // peak date the worst decline fell from (null if never fell)
  max_drawdown_end: string | null;    // trough date
  up_months: number;
  down_months: number;
  points: SaaReturnsSeriesPoint[];   // cumulative return
  drawdown: SaaDrawdownPoint[];      // per-date drawdown curve
  monthly: SaaMonthlyReturn[];       // year×month table cells
  yearly: SaaYearlyReturn[];         // year totals
  /** P20: CAGR over fixed trailing 1/3/5/7/10 years ending at `last` (null = history too short). */
  trailing_cagr?: { years: number; cagr: number | null }[];
  /** P20 charts (2026-10-11): the chosen window's complete months (first day / last day). */
  months_from?: string | null;
  months_to?: string | null;
  /** Every 1/3/5/7/10-year window over those months, stepping one month (annualised). */
  rolling?: SaaRollingSeries[];
  /** Best / worst / median / mean of each horizon's windows (all null when none fits). */
  outcomes?: SaaOutcomeRange[];
  /** P17 (b′): the largest fall within any 12 months over the FULL stored history (what the
   *  AQ drawdown check tests against Q8), and whether each Q8 band would accept it. */
  max_drawdown_12m?: number;
  max_drawdown_12m_start?: string | null;
  max_drawdown_12m_end?: string | null;
  q8_fit?: { band: string; limit: number; fits: boolean }[];
  /** The 12-month fall over the chosen window (≤ 0), beside `max_drawdown` (peak to trough). */
  max_drawdown_12m_window?: number;
}
export interface SaaRollingSeries { years: number; points: { date: string; ret: number }[] }
export interface SaaOutcomeRange {
  years: number;
  windows: number;
  best: number | null;
  worst: number | null;
  median: number | null;
  mean: number | null;
  best_end: string | null;
  worst_end: string | null;
}
export interface SaaAnalytics { profiles: SaaProfileAnalytics[] }

function _fileNameFromCD(cd: string, fallback: string): string {
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}
async function _detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

export const saaApi = {
  profiles: () => api.get<SaaProfile[]>('/saa/profiles'),
  assetClasses: () => api.get<SaaAssetClass[]>('/saa/asset-classes'),
  allocations: (code: string) => api.get<SaaProfileMatrix>(`/saa/profiles/${code}/allocations`),

  createProfile: (b: SaaProfileCreate) => api.post<SaaProfile>('/saa/profiles', b),
  updateProfile: (code: string, b: SaaProfileUpdate) => api.patch<SaaProfile>(`/saa/profiles/${code}`, b),
  deleteProfile: (code: string) => api.delete<SaaProfile>(`/saa/profiles/${code}`),

  createAssetClass: (b: SaaAssetClassCreate) => api.post<SaaAssetClass>('/saa/asset-classes', b),
  updateAssetClass: (code: string, b: SaaAssetClassUpdate) => api.patch<SaaAssetClass>(`/saa/asset-classes/${code}`, b),
  deleteAssetClass: (code: string) => api.delete<SaaAssetClass>(`/saa/asset-classes/${code}`),

  upsertAllocation: (pcode: string, acode: string, b: SaaAllocationUpsert) =>
    api.put<SaaAllocationCell>(`/saa/profiles/${pcode}/allocations/${acode}`, b),

  // ── SAA daily-returns store (Item C) ──────────────────────────────────────
  returnsSeries: (profile: string, from?: string, to?: string) => {
    const qs = new URLSearchParams({ profile });
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return api.get<SaaReturnsSeries>(`/saa/returns/series?${qs.toString()}`);
  },

  /** Multi-profile analytics (#4 Inc 2): cumulative + drawdown curves, CAGR / vol /
   *  max-DD / up-down-months, and the year×month + yearly tables, over [from, to]
   *  (default = each profile's full span). Unknown codes are skipped server-side. */
  returnsAnalytics: (profiles: string[], from?: string, to?: string, years?: number) => {
    const qs = new URLSearchParams({ profiles: profiles.join(',') });
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    // P32: "Last N years" — each profile's last N years, ending at its own latest day.
    if (years) qs.set('years', String(years));
    return api.get<SaaAnalytics>(`/saa/returns/analytics?${qs.toString()}`);
  },

  /** Bulk import the daily-returns matrix. preview writes nothing; commit UPSERTs
   *  per (profile, date). Stateless between calls — keep the same File for commit. */
  async returnsImport(
    file: File, mode: SaaReturnsImportMode, onConflict: SaaReturnsImportConflict,
  ): Promise<SaaReturnsImportReport> {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('on_conflict', onConflict);
    const res = await fetch('/saa/returns/import', { method: 'POST', credentials: 'include', body: form });
    if (!res.ok) throw new Error(await _detailOf(res, 'The SAA returns file could not be processed.'));
    return res.json();
  },

  /** Download the header-only daily-returns template (.xlsx): Date + profile labels. */
  async returnsDownloadTemplate(): Promise<void> {
    const res = await fetch('/saa/returns/template', { method: 'GET', credentials: 'include' });
    if (!res.ok) throw new Error(await _detailOf(res, 'The template could not be generated.'));
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), _fileNameFromCD(cd, 'saa_daily_returns_template.xlsx'));
  },
};
